import React, { useState } from "react";
import * as Icons from "lucide-react";
import { Check, Zap, CalendarClock } from "lucide-react";
import { LOGISTICS as L } from "../../data/logistics";
import { Seo, faqLd } from "../../components/Seo";
import { CtaLink, SectionHeader, DarkCTA, FaqList, Reveal, CheckItem } from "../../components/Shared";
import DriverAppIllustration from "../../components/DriverAppIllustration";
import { ProblemSection, CapabilitiesSection, IndustriesSection } from "../../components/telematics/TelematicsSections";
import { DispatchBoardVisual, OrdersVisual, HandoffLeakVisual, DispatchVisual, RoutePlanVisual, NotifyVisual, BillingVisual } from "../../components/logistics/LogisticsVisuals";

const VISUALS = { orders: OrdersVisual, dispatch: DispatchVisual, route: RoutePlanVisual, driver: DriverAppIllustration, notify: NotifyVisual, billing: BillingVisual };

const Hero = () => (
  <section className="pt-12 pb-14 md:pt-16 md:pb-20 overflow-hidden" data-testid="logistics-hero">
    <div className="container-x grid lg:grid-cols-[1.05fr_1fr] gap-10 lg:gap-12 items-center">
      <div>
        <span className="eyebrow-pill fade-up">{L.hero.eyebrow}</span>
        <h1 className="h-display h-display-hero mt-5 fade-up delay-1">{L.hero.title}</h1>
        <p className="mt-5 max-w-xl text-base md:text-lg text-[var(--fr-ink-2)] leading-relaxed fade-up delay-2">{L.hero.subtitle}</p>
        <div className="mt-7 flex flex-wrap gap-3 fade-up delay-3">
          <CtaLink cta={L.hero.primary} className="btn btn-primary btn-lg" data-testid="hero-primary-cta" />
          <CtaLink cta={L.hero.secondary} className="btn btn-outline btn-lg" data-testid="hero-secondary-cta" />
        </div>
        <ul className="mt-5 flex flex-wrap gap-x-5 gap-y-1.5 text-sm text-[var(--fr-muted)] fade-up delay-3" data-testid="hero-trust">
          {L.hero.trust.map((t) => (
            <li key={t} className="inline-flex items-center gap-1.5">
              <Check size={14} className="text-[var(--fr-primary)]" strokeWidth={2.5} /> {t}
            </li>
          ))}
        </ul>
      </div>
      <div className="fade-up delay-2 lg:max-w-[560px] lg:ml-auto w-full" data-testid="hero-visual">
        <div className="card p-3 shadow-[0_30px_60px_-30px_rgba(14,14,16,0.25)]">
          <div className="img-tile aspect-[6/5] bg-white">
            <DispatchBoardVisual className="w-full h-full" />
          </div>
        </div>
      </div>
    </div>
  </section>
);

const ModesSection = ({ data }) => (
  <section className="section" id="modes" data-testid="logistics-modes">
    <div className="container-x">
      <SectionHeader eyebrow={data.eyebrow} title={data.title} subtitle={data.subtitle} className="max-w-4xl" />
      <div className="mt-12 grid md:grid-cols-2 gap-4">
        {data.lanes.map((lane, i) => {
          const dark = i === 0;
          const LaneIcon = dark ? Zap : CalendarClock;
          return (
            <Reveal key={lane.id} delay={i * 100} className={`${dark ? "dark-block" : "card"} p-7 md:p-9`} data-testid={`logistics-lane-${lane.id}`}>
              <div className="flex items-center gap-3">
                <span className={`w-10 h-10 rounded-xl flex items-center justify-center ${dark ? "bg-white/10 text-white" : "bg-[var(--fr-primary-tint)] text-[var(--fr-primary-dark)]"}`}>
                  <LaneIcon size={18} />
                </span>
                <div>
                  <div className={`eyebrow ${dark ? "text-white/60" : ""}`}>{lane.label}</div>
                  <h3 className="font-display font-bold text-xl md:text-2xl leading-tight mt-0.5">{lane.tagline}</h3>
                </div>
              </div>
              <p className={`mt-5 text-sm ${dark ? "text-white/70" : "text-[var(--fr-muted)]"}`}>{lane.examples}</p>
              <ul className={`mt-6 space-y-3 ${dark ? "[&_svg]:!text-[#C9C4FF]" : ""}`}>
                {lane.points.map((p) => (
                  <CheckItem key={p}>{p}</CheckItem>
                ))}
              </ul>
            </Reveal>
          );
        })}
      </div>
      <Reveal delay={200} className="mt-4 card p-5 flex flex-wrap items-center gap-x-8 gap-y-2" data-testid="logistics-shared">
        <span className="eyebrow">Unified platform</span>
        {data.shared.map((s) => (
          <span key={s} className="inline-flex items-center gap-1.5 text-sm font-semibold">
            <Check size={14} className="text-[var(--fr-primary)]" strokeWidth={2.5} /> {s}
          </span>
        ))}
      </Reveal>
    </div>
  </section>
);

const FleetSection = ({ data }) => (
  <section className="section" data-testid="logistics-fleet">
    <div className="container-x">
      <SectionHeader eyebrow={data.eyebrow} title={data.title} />
      <div className="mt-12 grid md:grid-cols-3 gap-4">
        {data.items.map((it, i) => {
          const I = Icons[it.icon] || Icons.Circle;
          return (
            <Reveal key={it.title} delay={i * 80} className="card card-hover p-6" data-testid={`logistics-fleet-${i}`}>
              <div className="w-11 h-11 rounded-xl bg-[var(--fr-ink)] text-white flex items-center justify-center">
                <I size={20} />
              </div>
              <h3 className="font-display font-bold mt-5 text-lg">{it.title}</h3>
              <p className="mt-2 text-sm text-[var(--fr-ink-2)] leading-relaxed">{it.text}</p>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const AnalyticsSection = ({ data }) => {
  const [shot, setShot] = useState(data.shots[0]);
  return (
    <section className="section pt-0" data-testid="logistics-analytics">
      <div className="container-x">
        <Reveal className="dark-block px-6 py-14 md:px-14 md:py-20 overflow-hidden">
          <div className="grid lg:grid-cols-[1fr_1.25fr] gap-10 lg:gap-14 items-center">
            <div>
              <span className="eyebrow text-white/60">{data.eyebrow}</span>
              <h2 className="h-section text-white mt-4">{data.title}</h2>
              <p className="mt-5 text-base md:text-lg text-white/70 leading-relaxed">{data.subtitle}</p>
              <div className="mt-8 grid grid-cols-2 gap-x-6 gap-y-6">
                {data.kpis.map((k, i) => (
                  <Reveal key={k.value} delay={i * 60} className="border-l-2 border-[#C9C4FF]/40 pl-4" data-testid={`logistics-kpi-${i}`}>
                    <div className="font-display font-bold text-2xl md:text-3xl tracking-tight text-white leading-none">{k.value}</div>
                    <div className="mt-1.5 text-xs md:text-sm text-white/60 leading-snug">{k.label}</div>
                  </Reveal>
                ))}
              </div>
              <p className="mt-6 text-xs text-white/40">Typical ranges — your baseline sets the exact numbers; we measure them with you.</p>
            </div>
            <Reveal delay={150} className="relative">
              <div className="flex flex-wrap gap-2 mb-4" role="tablist" data-testid="logistics-analytics-tabs">
                {data.shots.map((sh) => (
                  <button
                    key={sh.id}
                    type="button"
                    role="tab"
                    aria-selected={shot.id === sh.id}
                    onClick={() => setShot(sh)}
                    className={`h-8 px-3.5 rounded-full text-xs font-semibold border transition-colors ${shot.id === sh.id ? "bg-white text-[var(--fr-ink)] border-white" : "border-white/15 text-white/70 hover:border-white/40"}`}
                    data-testid={`logistics-analytics-tab-${sh.id}`}
                  >
                    {sh.label}
                  </button>
                ))}
              </div>
              <h3 key={shot.id} className="font-display font-bold text-xl md:text-2xl leading-tight text-white mb-4 fade-up" data-testid="logistics-analytics-headline">
                {shot.headline[0]} <span className="text-[#C9C4FF]">{shot.headline[1]}</span>
              </h3>
              <div className="rounded-2xl bg-white shadow-[0_40px_80px_-30px_rgba(0,0,0,0.6)] overflow-hidden border border-white/10" data-testid="logistics-analytics-shot">
                <div className="h-8 bg-[#F4F4F0] flex items-center gap-1.5 px-3 border-b border-[var(--fr-border)]">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#E5484D]/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#F59E0B]/70" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#16A34A]/70" />
                  <span className="ml-3 h-4 flex-1 max-w-[260px] rounded bg-white border border-[var(--fr-border)] text-[9px] text-[var(--fr-muted)] px-2 leading-4">app.fleetroot.com · data analytics · {shot.label.toLowerCase()}</span>
                </div>
                <div className="bg-[#F7F7FB] p-2 sm:p-3">
                  <img key={shot.id} src={shot.src} alt={shot.alt} loading="lazy" className="w-full h-auto block rounded-lg fade-up" />
                </div>
              </div>
              <div key={`${shot.id}-stats`} className="mt-4 grid grid-cols-3 gap-3 fade-up delay-1" data-testid="logistics-analytics-stats">
                {shot.stats.map(([v, l]) => (
                  <div key={l} className="rounded-xl bg-white/5 border border-white/10 px-3 py-2.5">
                    <div className="font-display font-bold text-sm md:text-lg text-white leading-tight">{v}</div>
                    <div className="mt-0.5 text-[10px] md:text-xs text-white/55 leading-snug">{l}</div>
                  </div>
                ))}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-white/45">
                <span className="inline-flex items-center gap-1.5 font-semibold uppercase tracking-wider text-[#C9C4FF]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C9C4FF] animate-pulse" /> {data.caption}
                </span>
                <span>Also in the module: {data.more.join(" · ")}</span>
              </div>
            </Reveal>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default function LogisticsOrchestration() {
  return (
    <>
      <Seo title={L.seo.title} description={L.seo.description} jsonLd={faqLd(L.faq)} />
      <Hero />
      <ProblemSection data={L.problem} visual={HandoffLeakVisual} />
      <ModesSection data={L.modes} />
      <div className="bg-[var(--fr-bg-alt)]">
        <CapabilitiesSection data={L.stages} visuals={VISUALS} />
      </div>
      <FleetSection data={L.fleet} />
      <AnalyticsSection data={L.reports} />
      <IndustriesSection data={L.industries} />
      <section className="section" data-testid="logistics-faq">
        <div className="container-x grid lg:grid-cols-[1fr_1.4fr] gap-12">
          <SectionHeader eyebrow="FAQ" title="Questions operations teams ask us." />
          <Reveal delay={100}>
            <FaqList items={L.faq} testid="logistics-faq-list" />
          </Reveal>
        </div>
      </section>
      <DarkCTA title={L.cta.title} text={L.cta.text} cta={L.cta.cta} secondary={L.cta.secondary} />
    </>
  );
}
